import beginnersVolleyball from "@/assets/beginners-volleyball.jpg.asset.json";
import improvForBeginners from "@/assets/improv-for-beginners.jpg.asset.json";
import introToChoir from "@/assets/intro-to-choir.jpg.asset.json";
import coloradoRiver from "@/assets/colorado-river.jpg";
import balletBeginners from "@/assets/ballet-beginners.jpg";
import photographyClass from "@/assets/photography-class.jpg";
import chickensEggs from "@/assets/chickens-eggs.jpg";
import partnerDance from "@/assets/partner-dance.jpg";
import countryDancing from "@/assets/country-dancing.jpg";
import mommyMeCrochet from "@/assets/mommy-me-crochet.jpg";
import mommyMeSelfDefense from "@/assets/mommy-me-self-defense.jpg.asset.json";
import lineDancing from "@/assets/line-dancing.jpg.asset.json";
import hoovedAndHens from "@/assets/hooved-and-hens.jpg";
import legoRobotics from "@/assets/lego-robotics.jpg";

import mommyMeBaking2 from "@/assets/mommy-me-baking-2.jpg";


import chessClub from "@/assets/chess-club.jpg";
import beginnersMechanics from "@/assets/beginners-mechanics.jpg";
import beginnersWoodworking from "@/assets/beginners-woodworking.jpg";
import beginnersMetalworking from "@/assets/beginners-metalworking.jpg";
import beginnersMetalWorking from "@/assets/beginners-metal-working.jpg";
import peClass from "@/assets/pe-class.jpg";
import beginnersBasketball from "@/assets/beginners-basketball.jpg";




export interface ClassSession {
  label: string;
  dates: string;
  /** Session-specific venue override (canonical location resolver input). */
  location?: string | null;
  /** Session-specific room override. */
  room?: string | null;
  price?: number; // optional per-session price override
  full?: boolean; // when true, this session is unavailable for registration
  /**
   * When true, this session is closed to NEW registration. Existing families
   * stay enrolled and the session keeps showing its dates publicly — it simply
   * cannot be purchased anymore.
   */
  closed?: boolean;
}

// Canonical billing unit lives in src/lib/classPricing.ts. Re-exported here
// under the legacy alias so files that still import `SessionUnit` keep
// compiling — new code should import `BillingUnit` from `@/lib/classPricing`.
export type { BillingUnit } from "@/lib/classPricing";
export type SessionUnit = "per_session" | "per_month" | "per_semester";

export interface SupplyItem {
  name: string;
  url: string;
  required?: boolean;
}

export interface SupplyList {
  required?: SupplyItem[];
  recommended?: SupplyItem[];
  note?: string;
}

export interface ClassItem {
  id: string;
  title: string;
  description: string;
  shortDescription?: string;
  ages: string;
  schedule: string;
  duration: string;
  price: string;
  unitPrice: number;
  startDate: string;
  status: "open" | "ended" | "coming-soon" | "instructor-wanted" | "waitlist";
  image: string;
  category: string;
  /** Class-level canonical venue (e.g. "Grace Community Church"). */
  location?: string | null;
  /** Class-level canonical room (e.g. "Social Room"). */
  room?: string | null;
  weeklyBreakdown?: string[];
  sessions?: ClassSession[];
  externalSignupUrl?: string;
  supplyList?: SupplyList;
  /** @deprecated Phase 1 lifecycle: visibility now lives in `class_registry`. Field retained for schema-compat only; no runtime code reads it. */
  hiddenFromCalendar?: boolean;
  /** @deprecated Phase 1 lifecycle: visibility now lives in `class_registry`. Field retained for schema-compat only; no runtime code reads it. */
  hidden?: boolean;

  /** Canonical billing unit. Legacy field name `sessionUnit` retained. */
  billingUnit?: import("@/lib/classPricing").BillingUnit;

  /**
   * Who manages this class.
   * - `silver_sage` (default): Silver Sage handles instructor, registration, checkout.
   * - `external_provider`: Outside provider handles instruction and registration. Silver Sage
   *   only hosts the listing. Instructor is not required. Register via `externalSignupUrl`.
   */
  managementType?: "silver_sage" | "external_provider";
  providerName?: string;
  providerContactEmail?: string;
  providerContactPhone?: string;
  providerPublicContactText?: string;

  /** Underlying database class UUID (only present for DB-sourced classes). Used to look up schedule options and other related rows. */
  dbId?: string;

  /** True when a real (non-placeholder) instructor is currently assigned. Drives
   *  "Pricing coming soon" behavior across every pricing surface. */
  hasInstructor?: boolean;
  /** For per_session pricing: how many weeks a single session/bundle covers. Defaults to 1 (per-class). */
  sessionLengthWeeks?: number;
  /**
   * Raw `price_display` override as stored in the DB (never the resolved
   * label). Used only by the Pricing Health audit so it can compare against
   * the structured price without seeing the auto-formatted string echoed back.
   * Undefined for legacy static-only classes.
   */
  priceDisplayRaw?: string | null;
  /** Admin has confirmed the public display intentionally omits the billing unit. */
  displayOmitsUnitApproved?: boolean;
  /**
   * Configured seat capacity from the Class Editor. When set (> 0) it is the
   * authoritative capacity everywhere; otherwise the standard fallback applies.
   */
  capacity?: number | null;
}

export const classes: ClassItem[] = [

  // Hooves & Hens (featured)
  {
    id: "hooved-and-hens",
    title: "Hands on with Hooves & Hens",
    description:
      "Step off the sidewalk and into the barnyard! In this unique, hands-on class held at a local farm, kids will get up close and personal with horses, goats, chickens, and more. Students will learn the real-world skills of animal care — from feeding and grooming to mucking stalls and collecting eggs. No riding is involved, but plenty of bonding with the animals is guaranteed! This is a wonderful opportunity for children to build responsibility, compassion, and confidence while connecting with nature and the animals that call the farm home.",
    ages: "6-12",
    schedule: "Tuesdays & Fridays 8:00 AM – 9:00 AM",
    duration: "4 Weeks",
    price: "$150",
    unitPrice: 150,
    startDate: "July",
    status: "open" as const,
    image: hoovedAndHens,
    category: "Nature & Science",
    sessions: [
      { label: "Session 3", dates: "Jul 21 – Aug 15 – Tuesdays & Fridays 8:00–9:00 AM", price: 150, full: true },
      { label: "Fall Semester", dates: "September – December 2026 — Tuesdays & Fridays 9:00 AM – 10:30 AM", price: 150 },
    ],
    hiddenFromCalendar: true,
  },
  // Mommy & Me Baking Class
  {
    id: "mommy-me-baking-2",
    title: "Mommy & Me Baking Class",
    description:
      "A fun baking adventure for parent-child duos! Build confidence in the kitchen with hands-on baking projects designed to teach new techniques, recipes, and lasting memories. Perfect for families who want more time mixing, baking, and bonding together.",
    ages: "All ages (with parent)",
    schedule: "Mondays 10:00 AM – 12:00 PM",
    duration: "4 Weeks (2 hours each)",
    price: "$40",
    unitPrice: 40,
    startDate: "August 10, 2026",
    status: "open" as const,
    image: mommyMeBaking2,
    category: "Life Skills",
    sessions: [
      {
        label: "August 2026",
        dates:
          "August 10, 17, 24, 31, 2026 — Mondays 10:00 AM – 12:00 PM — Grace Community Church (further location details provided after registration is complete)",
      },
    ],
    hiddenFromCalendar: false,
  },
  // P.E. Class
  {
    id: "pe-class",
    title: "P.E. Class",
    description:
      "Get your children up and active! This course provides an opportunity for kids of all ages to explore the joy of movement through games, team sports, and skill-building activities designed just for them. Students will develop coordination, strength, balance, and endurance while learning important lessons in teamwork, sportsmanship, and fair play. With a focus on safety, inclusion, and positive encouragement, this class helps kids build confidence, stay healthy, and discover how great it feels to be active. Whether they're running, jumping, throwing, or dancing, every child will leave each session energized, smiling, and proud of what their body can do!",
    shortDescription:
      "Get your children up and active! This course provides an opportunity for kids of all ages to explore the joy of movement through games, team sports, and skill-building activities designed just for them. Students will develop coordination, strength, balance, and endurance while learning important lessons in teamwork, sportsmanship, and fair play. With a focus on safety, inclusion, and positive encouragement, this class helps kids build confidence, stay healthy, and discover how great it feels to be active. Whether they're running, jumping, throwing, or dancing, every child will leave each session energized, smiling, and proud of what their body can do!",
    ages: "6+",
    schedule: "Wednesdays or Fridays",
    duration: "4 Weeks",
    price: "$40",
    unitPrice: 40,
    startDate: "August",
    status: "open" as const,
    image: peClass,
    category: "Physical Activity",
    sessions: [
      { label: "Fridays (August)", dates: "Fridays 3:00 – 4:30 PM — Location TBD", price: 40 },
      { label: "Wednesdays (August)", dates: "Wednesdays 9:30 AM – 11:00 AM — Location TBD", price: 40 },
    ],
    weeklyBreakdown: [
      "Week 1: Movement Fundamentals & Sport Selection — Students start with warm-up stretches and activities, after which activities focused on running, jumping, balancing, throwing, and basic coordination are introduced. Later in the class, kids will have the opportunity to vote and choose which sports and games they would like to focus on for the remaining weeks. This helps ensure the program is exciting and tailored to their interests.",
      "Week 2: Skill Building — Building on the fundamentals and the sports chosen in Week 1, students will work on skills specific to their selected activities. Fun small-group games keep energy high while improving coordination and confidence.",
      "Week 3: Continued Skill Building — This week continues the process of skill building through team-based games. Emphasis is placed on teamwork, communication, fair play, and applying the skills learned in the first two weeks.",
      "Week 4: Showcase — In the final week, students enjoy a high-energy mix of their favorite chosen sports and games. We combine activities from all four weeks in an exciting demonstration of skills learned, ending with a celebration of their growth, effort, and progress.",
    ],
    hiddenFromCalendar: true,
  },
  // Beginner's Basketball (September)
  {
    id: "beginners-basketball",
    title: "Beginner's Basketball",
    description:
      "A fun, active introduction to basketball for homeschoolers ages 10 and up! Students will learn the fundamentals — dribbling, passing, shooting, and teamwork — through drills and friendly scrimmages. Held in the Fox Hall auditorium behind the church building. The instructor has committed to September for now; additional months may be added later.",
    ages: "10+",
    schedule: "Thursdays 4:00 PM – 5:00 PM",
    duration: "4 Weeks",
    price: "$40",
    unitPrice: 40,
    startDate: "September",
    status: "open" as const,
    image: beginnersBasketball,
    category: "Physical Activity",
    billingUnit: "per_month",
    sessions: [
      { label: "September", dates: "September 2026 — Thursdays 4:00 – 5:00 PM — Fox Hall auditorium", price: 40 },
    ],
    hiddenFromCalendar: true,
  },
  // 2. Pokémon Club (Biology of Lake Mead moved to bottom)
  {
    id: "pokemon-club",
    title: "Pokémon Club",
    description:
      "We'll be learning how to play the Pokémon card game, which includes strategy and strategic thinking through deck building and game play, as well as taking turns, addition and subtraction with 100's placements, as well as minor multiplication.",
    ages: "7-14",
    schedule: "Fridays 10:30 AM – 12:00 PM",
    duration: "4 Weeks",
    price: "$40",
    unitPrice: 40,
    startDate: "May 4 – August 28",
    status: "open" as const,
    image: "https://images.unsplash.com/photo-1613771404784-3a5686aa2be3?w=600",
    category: "Creative Arts",
    sessions: [
      { label: "Session 1", dates: "May 4 – May 29", full: true },
      { label: "Session 3", dates: "Jul 6 – Jul 31" },
      { label: "Session 4", dates: "Aug 3 – Aug 28" },
      { label: "Fall Semester", dates: "September – December 2026 — Fridays 10:30 AM – 12:00 PM" },
    ],
  },
  // 3. Mommy & Me Crochet
  {
    id: "mommy-me-crochet",
    title: "Mommy & Me Crochet",
    description:
      "Grab your hooks and yarn — it's time to crochet together! In this cozy, hands-on class, parents and children will learn crochet techniques side by side, completing a brand-new project each 4-week session. From bucket hats to stuffed animals, each session builds skills while creating something wonderful to take home. No experience needed — just patience, creativity, and a willingness to learn together!",
    ages: "All ages (with parent)",
    schedule: "Fridays 1:00 PM – 2:00 PM",
    duration: "4 Weeks",
    price: "$40",
    unitPrice: 40,
    startDate: "Session 1: May 4 – May 29 | Session 2: Jun 1 – Jun 26 | Session 3: Jul 6 – Jul 31 | Session 4: Aug 7 – Aug 28",
    status: "open",
    image: mommyMeCrochet,
    category: "Creative Arts",
    sessions: [
      { label: "Session 2", dates: "Jun 1 – Jun 26", full: true },
      { label: "Session 3", dates: "Jul 6 – Jul 31" },
      { label: "Session 4", dates: "August Session — Aug 7, 14, 21, 28" },
      { label: "Session 1", dates: "May 4 – May 29", full: true },
      { label: "Fall Semester", dates: "September – December 2026 — Fridays 1:00 – 2:00 PM" },
    ],
    weeklyBreakdown: [
      "Session 1 — Bucket Hat: Week 1: Learn the foundation chain, single crochet, and basic techniques needed to start your bucket hat. Week 2: Refine your stitches, troubleshoot challenges, and continue building your hat. Week 3: Shape the brim and begin finishing details. Week 4: Complete your bucket hat — celebrate your creation!",
      "Session 2 — Mock Water Balloons: Week 1: Learn the magic ring, increases, and shaping techniques for crocheting in the round. Week 2: Refine your technique and work through any tricky spots together. Week 3: Start stuffing and closing your water balloons. Week 4: Finish your set of mock water balloons — celebration time!",
      "Session 3 — Stuffy (Stuffed Animal): Week 1: Learn amigurumi basics — crocheting body parts in the round. Week 2: Continue building your stuffy and share progress with the group. Week 3: Assemble and start adding finishing touches. Week 4: Complete your stuffy and show it off — celebration!",
      "Session 4 — Choice Project (Hat, Ear Warmers, or Fingerless Gloves): Week 1: Choose your project and learn the specific techniques needed. Week 2: Build on your skills and keep your project moving. Week 3: Begin finishing and personalizing your piece. Week 4: Finish your chosen project — final celebration of the summer!",
    ],
    supplyList: {
      required: [
        { name: "Crochet Project Bag — our favorite for crocheting on the go", url: "https://amzn.to/4n3k041" },
      ],
      note: "A bag for holding your supplies is required — any bag will do! Here is our favorite bag for crocheting on the go.",
    },
  },
  // Mommy & Me Self Defense (coming soon)
  {
    id: "mommy-me-self-defense",
    title: "Mommy & Me Self Defense",
    description:
      "Build confidence, awareness, and practical safety skills alongside your teen! In this empowering class, mothers and daughters will learn basic self-defense techniques, situational awareness, and verbal boundary-setting from a trained instructor. Together, you'll practice simple, effective moves in a supportive, judgment-free environment. No prior experience needed — just come ready to learn, move, and grow stronger together. Dates, times, and pricing are still being finalized — submit your interest below and we'll be in touch as soon as details are available!",
    ages: "13+",
    schedule: "TBD",
    duration: "TBD",
    price: "TBD",
    unitPrice: 0,
    startDate: "Coming Soon",
    status: "coming-soon" as const,
    image: mommyMeSelfDefense.url,
    category: "Life Skills",
    hiddenFromCalendar: true,
  },
  // Mommy & Me Line Dancing (coming soon - August)
  {
    id: "mommy-me-line-dancing",
    title: "Mommy & Me Line Dancing",
    description:
      "Grab your boots and cowboy hats and hit the dance floor together! In this fun, beginner-friendly line dancing class, moms and kids ages 6+ will learn classic country line dances step by step. No prior dance experience needed — just come ready to move, laugh, and make memories. Details for August are still being finalized — submit your interest below and we'll be in touch as soon as dates, times, and pricing are set!",
    ages: "6+",
    schedule: "August — day/time TBD",
    duration: "TBD",
    price: "TBD",
    unitPrice: 0,
    startDate: "August",
    status: "coming-soon" as const,
    image: lineDancing.url,
    category: "Physical Activity",
    hidden: true,
  },
  {
    id: "lego-robotics",
    title: "LEGO Robotics Club",
    description:
      "Build it. Code it. Watch it move! In this exciting, hands-on class, young engineers will use LEGO WeDo 2.0 kits to build motorized creations and bring them to life through simple coding on a tablet. Students will explore the basics of robotics, engineering, and programming as they design machines that spin, walk, grab, and roll. Each session introduces new challenges and builds on previous skills, encouraging creativity, problem-solving, and teamwork. No prior experience needed — just a love of LEGO and a curiosity about how things work! Tablets are provided.",
    ages: "6-12",
    schedule: "Fridays 11:00 AM – 12:00 PM",
    duration: "4 Weeks",
    price: "$40",
    unitPrice: 40,
    startDate: "Session 1: May 4 – May 29 | Session 2: Jun 1 – Jun 26 | Session 3: Jul 6 – Jul 31 | Session 4: Aug 7 – Aug 28",
    status: "open",
    image: legoRobotics,
    category: "STEAM",
    sessions: [
      { label: "Session 1", dates: "May 4 – May 29", full: true },
      { label: "Session 3", dates: "Jul 6 – Jul 31" },
      { label: "Session 4", dates: "August Session — Aug 7, 14, 21, 28" },
      { label: "Fall Semester", dates: "Sep 4 – Dec 18, 2026 · Fridays 9:20–10:20 AM" },
    ],
    weeklyBreakdown: [
      "Week 1 — Introduction to WeDo 2.0: Students meet their LEGO WeDo 2.0 kits, learn how the pieces, motors, and sensors work, and build their first motorized creation. They'll get their first taste of coding by programming their build to move!",
      "Week 2 — Sensors & Reactions: Students explore motion and tilt sensors, building creations that can detect and respond to their environment. Challenges include making builds that stop, start, or change direction on their own.",
      "Week 3 — Design Challenge: Teams take on an engineering challenge — designing and coding a LEGO robot to complete a specific task. Creativity and collaboration take center stage as students iterate on their designs.",
      "Week 4 — Showcase & Free Build: Students choose their own project to design, build, and code from scratch. The session wraps up with a showcase where each student demonstrates their creation to the group!",
    ],
  },
  // 5. All About Farming
  {
    id: "all-about-farming",
    title: "All About Farming",
    description:
      "From seeds to supper, discover the wonderful world of farming! In this hands-on class, young learners will explore different types of farms, plant their own mini gardens, meet real chickens, learn about composting, and even run their own mock farmer's market. Each 4-week session covers a unique set of topics — from companion gardening and the magic of bees to nutrition taste tests and the story of Johnny Appleseed. Students will gain a deep appreciation for where our food comes from and the hard work that goes into growing it.",
    ages: "6-12",
    schedule: "Mondays 10:00 – 11:00 AM",
    duration: "4 Weeks",
    price: "$40",
    unitPrice: 40,
    startDate: "Session 1: May 4 – May 29 | Session 2: Jun 1 – Jun 26 | Session 3: Jul 6 – Jul 31",
    status: "open",
    image: chickensEggs,
    category: "Nature & Science",
    sessions: [
      { label: "Session 1", dates: "May 4 – May 29", full: true },
      { label: "Session 2", dates: "Jun 1 – Jun 26", full: true },
      { label: "Session 3", dates: "Jul 6 – Jul 31" },
      { label: "Fall Semester", dates: "Fall 2026 — dates coming soon" },
    ],
    weeklyBreakdown: [
      "Session 1, Week 1 — What Is a Farmer?: Exploring different types of farming — from crop farms to ranches — and what it means to be a farmer today.",
      "Session 1, Week 2 — Farm to Table: Where does our food come from? Students trace the journey from field to fork and learn about the food supply chain.",
      "Session 1, Week 3 — Let's Plant a Mini Garden!: Students get their hands dirty planting seeds and learning about companion gardening techniques.",
      "Session 1, Week 4 — Bees: The Unsung Heroes: Discover the incredible role bees play in pollination, agriculture, and honey production.",
      "Session 2, Week 1 — Farm Animals: A look into the animals that make a farm run and their contributions — from milk to wool.",
      "Session 2, Week 2 — Chicken Visit!: A real chicken visit! Students learn chicken anatomy and study the science of eggs.",
      "Session 2, Week 3 — Composting 101: Nature's own fertilizer — students learn how to turn scraps into soil gold.",
      "Session 2, Week 4 — 50 States of Agriculture: Fun facts about farming across America, plus the history of Victory Gardens.",
      "Session 3, Week 1 — CSAs, Orchards & Vines: What is a CSA? Students explore orchards, vineyards, and community-supported agriculture.",
      "Session 3, Week 2 — Mock Farmer's Market: Students plan and run their own farmer's market, practicing math, marketing, and teamwork.",
      "Session 3, Week 3 — Fruit or Vegetable?: A nutrition deep-dive with tastings! Students learn to classify produce and explore healthy eating.",
      "Session 3, Week 4 — Herbs, Sprouts & Johnny Appleseed: The story of Johnny Appleseed, an herb and sprout exploration, and an apple taste test to finish the session!",
    ],
  },
  {
    id: "chess-club",
    title: "Chess Club",
    description:
      "Sharpen your mind and master the board! This rigorous chess training class is designed for older students ready to take their game to the next level. Students will study classic openings, mid-game tactics, endgame strategy, and positional play through guided instruction, puzzles, and competitive match play. Each session builds on the last, developing deep analytical thinking, patience, and sportsmanship. Whether your student is an intermediate player looking to improve or an advanced thinker ready for serious competition, this club will challenge and inspire them.",
    ages: "10+",
    schedule: "Fridays 4:00 PM – 5:00 PM",
    duration: "3 Weeks",
    price: "$30",
    unitPrice: 30,
    startDate: "September 2026",
    // Reopened 2026-08-07 (owner approved) for the canonical September session only.
    status: "open" as const,
    image: chessClub,
    category: "Strategy & Logic",
    sessions: [
      // Owner-confirmed 2026-08-07: no August Chess class. September is three
      // meetings at $30 per child, Social Room inside Grace Community Church.
      {
        label: "September",
        dates:
          "September 11, 18, 25, 2026 (Fridays) — 4:00 PM – 5:00 PM — Grace Community Church, Social Room",
        price: 30,
      },
    ],
  },
  {
    id: "photography-for-teens",
    title: "Photography for Teens",
    description:
      "See the world through a new lens! In this inspiring class, students will learn the fundamentals of photography from a professional photographer. From understanding composition and lighting to mastering camera settings and editing basics, each session builds real-world skills that turn snapshots into art. Students will explore portrait, landscape, and creative photography through hands-on shooting assignments and constructive group critiques. Students will develop their unique artistic eye and walk away with a portfolio of images they're proud of. No experience needed — just curiosity and a love of capturing the world around you! Students are required to purchase this camera before the first class — it will be theirs to keep: https://www.amazon.com/dp/B0FS1GVYPS?ref=t_ac_view_request_product_image&campaignId=amzn1.campaign.24A9RVDJV8A7H&linkCode=tr1&tag=nevcon-20&linkId=amzn1.campaign.24A9RVDJV8A7H_1778013359780",
    ages: "12+",
    schedule: "TBD",
    duration: "4 Weeks",
    price: "$40",
    unitPrice: 40,
    startDate: "TBD",
    status: "coming-soon" as const,
    image: photographyClass,
    category: "Creative Arts",
    hiddenFromCalendar: true,
    supplyList: {
      required: [
        { name: "Canon PowerShot Camera", url: "https://www.amazon.com/dp/B0FS1GVYPS?ref=t_ac_view_request_product_image&campaignId=amzn1.campaign.24A9RVDJV8A7H&linkCode=tr1&tag=nevcon-20&linkId=amzn1.campaign.24A9RVDJV8A7H_1778013359780" },
      ],
    },
  },
  // Beginner's Mechanics — Auto 101 (interest only)
  {
    id: "beginners-mechanics",
    title: "Auto 101 — Beginner's Automotive",
    description:
      "Prepare your teen to be a confident, capable car owner. This course covers essential skills like tire safety, checking fluids, handling a dead battery, and recognizing early warning signs. The focus is on simple, practical knowledge that helps prevent breakdowns, avoid costly repairs, and stay safe. Dates, times, and pricing are still being finalized — submit your interest below and we'll be in touch as soon as details are available!",
    ages: "14+ (12+ with a parent present)",
    schedule: "Saturdays 10:00 AM – Noon",
    duration: "4 weeks",
    price: "$50 per session",
    unitPrice: 0,
    startDate: "Coming Soon",
    status: "coming-soon" as const,
    image: beginnersMechanics,
    category: "STEAM",
    hiddenFromCalendar: true,
    weeklyBreakdown: [
      "Week 1 – Tires (Safety & Control): Students will learn how to check tire pressure, inspect tread depth, and recognize when a tire may be unsafe. We will practice using a tire gauge and how to change a flat tire.",
      "Week 2 – Fluids (Basic Vehicle Care): Students will be introduced to the main fluids in a vehicle, including oil, coolant, and washer fluid. They will learn how to locate and check these safely, and understand why maintaining proper fluid levels is important.",
      "Week 3 – Car Won't Start (Basic Troubleshooting): This week focuses on common reasons a car may not start. Students will learn how to identify a weak or dead battery and practice the proper way to safely jump-start a vehicle.",
      "Week 4 – Warning Signs (Awareness & Prevention): Students will learn to recognize common dashboard warning lights, unusual sounds, and other signs that something may be wrong with a vehicle. The goal is to help them know when to take action and avoid bigger problems.",
    ],
    supplyList: {
      required: [
        { name: "Safety Work Gloves MicroFoam Nitrile Coated", url: "https://amzn.to/4t6UBHS" },
        { name: "Disposable Nitrile Gloves", url: "https://amzn.to/4eUELg7" },
      ],
      recommended: [
        { name: "4 Pack Telescoping Magnet Pick-up Tool Set", url: "https://amzn.to/42bmPX5" },
        { name: "Ztek Safety Glasses", url: "https://amzn.to/4urCw8N" },
        { name: "Headlamp Flashlight", url: "https://amzn.to/3QYDdYx" },
        { name: "Spark Plug Socket Wrench T-Handle", url: "https://amzn.to/4wloHun" },
        { name: "Battery Terminal Cleaners", url: "https://amzn.to/426YWQA" },
        { name: "Digital Multimeter Tester", url: "https://amzn.to/3QKRVCm" },
        { name: "Steel Jack Auto Stands", url: "https://amzn.to/4eRU7lt" },
        { name: "Floor Jack, Hydraulic Portable Car Lift Jack", url: "https://amzn.to/4ujpcmu" },
        { name: "1/2 Inch Drive Click Torque Wrench", url: "https://amzn.to/4cVLG67" },
        { name: "Telescoping Lug Wrench", url: "https://amzn.to/4cIsMRp" },
        { name: "Oil Funnel Set", url: "https://amzn.to/42FprfU" },
        { name: "Oil Filter Pliers", url: "https://amzn.to/4cSEqtb" },
        { name: "Pliers Set", url: "https://amzn.to/42HRtYc" },
        { name: "Ratcheting Wrench Set", url: "https://amzn.to/4uj3YFr" },
        { name: "Drive Socket Set", url: "https://amzn.to/4eEtKj9" },
      ],
      note: "Recommended items are optional — only purchase if you'd like your student to have their own set of tools.",
    },
  },
  // Beginner's Woodworking (interest only)
  {
    id: "beginners-woodworking",
    title: "Beginner's Woodworking",
    description:
      "Discover the satisfaction of building with your own two hands! In this introductory woodworking class, students will learn shop safety, how to use basic hand tools, measure and mark accurately, and complete simple woodworking projects from start to finish. A great way to build confidence, patience, and craftsmanship. Dates, times, and pricing are still being finalized — submit your interest below and we'll be in touch as soon as details are available!",
    ages: "12+",
    schedule: "TBD",
    duration: "TBD",
    price: "TBD",
    unitPrice: 0,
    startDate: "Coming Soon",
    status: "coming-soon" as const,
    image: beginnersWoodworking,
    category: "STEAM",
  },
  // Beginner's Metal Working (interest only)
  {
    id: "beginners-metal-working",
    title: "Beginner's Metal Working",
    description:
      "A hands-on introduction to the world of metal! Students will learn shop safety, how to measure and mark metal, and get an introduction to basic techniques like cutting, shaping, and joining. A great way to build confidence and craftsmanship while exploring a new medium. Dates, times, and pricing are still being finalized — submit your interest below and we'll be in touch as soon as details are available!",
    ages: "12+",
    schedule: "TBD",
    duration: "TBD",
    price: "TBD",
    unitPrice: 0,
    startDate: "Coming Soon",
    status: "coming-soon" as const,
    image: beginnersMetalWorking,
    category: "STEAM",
  },
  // Beginner's Metalworking (external signup via Team Alini)
  {
    id: "beginners-metalworking",
    title: "Beginner's Metalworking",
    description:
      "Taught by Chris at Team Alini, this homeschool metalworking program offers four hands-on tracks for teens: 1) Basic Metalworking — learn to use metal shears, a metal break, shrinker/stretchers, and an English wheel to build a custom name sign with riveted nameplate and polished aluminum (max 2 students). 2) CNC Programming & Operation (1-on-1) — learn how CNC machines work, what G-code is, and create a project that engraves an image into metal, plus cutting and milling techniques. 3) Basic Welding (1-on-1, age 13+) — use proper safety equipment to weld steel using MIG (or TIG upon MIG completion). 4) Hand Engraving (1-on-1, age 13+) — hand engrave metal pendants using both manual hand-push and pneumatic handpiece techniques, generating finished pendants that inspire jewelry making. Tracks 2–4 recommend completing Basic Metalworking first. Each class is 8 hours and can be split across multiple days. Materials and safety equipment supplied; lunch from Subway provided for full-day students. Students must wear jeans and closed-toe shoes (no sandals, flip-flops, or shorts). The classroom is air-conditioned. Fees: $150 per full day, payment required prior to start. Scheduling is arranged directly with the provider — use the signup link below.",
    ages: "12+ (Welding & Engraving 13+)",
    schedule: "Flexible — scheduled with instructor",
    duration: "8 hours per class (split across days as needed)",
    price: "$150 per full day",
    unitPrice: 150,
    startDate: "Contact to schedule",
    status: "open" as const,
    image: beginnersMetalworking,
    category: "STEAM",
    providerName: "Chris — Team Alini",
    externalSignupUrl: "https://www.teamalini.com/homeschool.php",
  },
  // Biology of Lake Mead (interest only)
  {
    id: "biology-lake-mead",
    title: "Biology of Lake Mead & the Colorado River",
    description:
      "We will explore the insects, fish, birds, plants and hydrology of Lake Mead and the Colorado River in the classroom and by kayak at the lake and river! We will look at invertebrates living in our local aquatic ecosystem by microscope, learn about seasonal bird migration, native and non-native fish species, and how the dam affected the Colorado River ecosystem. Dates, times, and pricing are still being finalized — submit your interest below and we'll be in touch as soon as details are available!",
    ages: "10+",
    schedule: "Wednesdays 10:00 - 11:30 AM",
    duration: "4 Weeks",
    price: "$199",
    unitPrice: 0,
    startDate: "Coming Soon",
    status: "coming-soon" as const,
    image: coloradoRiver,
    category: "Nature & Science",
    weeklyBreakdown: [
      "Week 1 — Lake Mead Invertebrates: We'll discuss invertebrate life cycles and look at them up close under a microscope!",
      "Week 2 — Hydrology of the Colorado River System: Students will compare and contrast what the Colorado River looked like before the dams and after using sand models, and how the dams along the Colorado River have affected the ecosystem.",
      "Week 3 — Fish of the Colorado: We will explore the native and non-native fish of the Colorado River. We will talk about what fish are native and how the dams and non-native fish have interacted over time. This class will include an art project!",
      "Week 4 — Kayaking: Students will explore and see first hand the history and ecosystem of the Colorado River ecosystem. We will kayak to see historical sites from the building of the dam, look for birds and butterflies, and hopefully see fish!",
    ],
  },
  // Intro to Choir (interest only)
  {
    id: "intro-to-choir",
    title: "Intro to Choir",
    description:
      "Discover the joy of making music together! Intro to Choir is a fun and encouraging class where students will build confidence through singing while learning the fundamentals of vocal music. Students will explore proper breathing, pitch matching, rhythm, harmony, and basic music reading in a relaxed, supportive environment. Along the way, they'll develop teamwork, listening skills, and stage confidence as they prepare simple songs to share with family and friends.\n\nNo previous musical experience is required—just a willingness to sing, learn, and have fun!\n\nDates, times, and pricing are still being finalized — submit your interest below and we'll be in touch as soon as details are available!",
    ages: "7-12",
    schedule: "TBD",
    duration: "TBD",
    price: "TBD",
    unitPrice: 0,
    startDate: "TBD",
    status: "coming-soon" as const,
    image: introToChoir.url,
    category: "Creative Arts",
  },
  // Improv for Beginners (interest only)
  {
    id: "improv-for-beginners",
    title: "Improv for Beginners",
    description:
      "Step into the spotlight and say yes! In this playful, low-pressure class, middle school and high school students will learn the basics of improvisational theatre through games, scene work, and creative collaboration. Students will build confidence, quick thinking, active listening, and public speaking skills while laughing and creating scenes together on the spot. No scripts, no experience, no pressure — just fun!\n\nDates, times, and pricing are still being finalized — submit your interest below and we'll be in touch as soon as details are available!",
    ages: "11-18",
    schedule: "TBD",
    duration: "TBD",
    price: "TBD",
    unitPrice: 0,
    startDate: "TBD",
    status: "coming-soon" as const,
    image: improvForBeginners.url,
    category: "Creative Arts",
  },
  // Beginners Volleyball (interest only)
  {
    id: "beginners-volleyball",
    title: "Beginners Volleyball",
    description:
      "Bump, set, spike! This fun introductory volleyball class is designed for middle school students who want to learn the fundamentals of the game in a supportive, team-focused environment. Students will practice proper passing and bumping technique, setting, serving, footwork, and court positioning through engaging drills and mini-games. Along the way, they'll build coordination, communication, teamwork, and confidence — no prior experience needed. Whether your student is brand new to volleyball or looking to strengthen their basics, this class is a great place to start!\n\nDates, times, and pricing are still being finalized — submit your interest below and we'll be in touch as soon as details are available!",
    ages: "10-18",
    schedule: "TBD",
    duration: "TBD",
    price: "TBD",
    unitPrice: 0,
    startDate: "TBD",
    status: "coming-soon" as const,
    image: beginnersVolleyball.url,
    category: "Sports & Fitness",
  },
  // Dam Good Cruisers – E-Bike Club (Fall 2026, free, volunteer-led)
  {
    id: "dam-good-cruisers-e-bike-club",
    title: "Dam Good Cruisers – E-Bike Club",
    description:
      "A free, volunteer-led e-bike club for homeschool families. Riders meet Saturday mornings for group rides on Boulder City trails and greenways. Locations vary each week — details, route maps, and weather updates are posted in the club's BAND group. All rides begin promptly at 10:00 AM. Helmets required; a working e-bike or bicycle in safe condition is required. Adult supervision expected for younger riders.\n\nJoin the BAND group to get weekly meeting locations and route details: https://band.us/",
    shortDescription:
      "Free Saturday morning group e-bike rides for homeschool families. Locations vary — routes posted weekly in the club's BAND group.",
    ages: "8+ with a parent",
    schedule: "Saturdays at 10:00 AM",
    duration: "Fall 2026 semester (weekly)",
    price: "FREE",
    unitPrice: 0,
    startDate: "Begins September 12, 2026",
    status: "open" as const,
    image: "",
    category: "Clubs",
    weeklyBreakdown: [
      "Week 1 (Sep 12) — Meet & greet ride, route TBD",
      "Weekly through fall — Check BAND for meeting location, route, and weather updates",
    ],
  },
];
